import { useEffect, useState } from "react";
import { X, PencilSimple, ChartBar } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const STATUS = ["Lunas", "Sebagian", "Belum"];
const QUICK_NOTES = ["Transfer", "Cash", "Jazakumullahu Khairan", "Dibayar sebagian", "Menunggu konfirmasi"];
const badge = (st) => st === "Lunas" ? "bg-green-500 text-white" : st === "Sebagian" ? "bg-yellow-400 text-yellow-900" : "bg-red-500 text-white";
const label = (st) => (st === "Belum" ? "Belum" : st);
const rupiah = (n) => `Rp ${(Number(n) || 0).toLocaleString("id-ID")},-`;
const fmtPaid = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }); } catch { return "-"; } };

export default function ManageSPP() {
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null); // month being edited
  const [viewOpen, setViewOpen] = useState(false);
  const [ef, setEf] = useState({ status: "Belum", paid_date: "", note: "", amount: 400000 });
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const YEARS = [nowYear - 1, nowYear, nowYear + 1];
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSD = user?.school === "sdqu";
  const logo = isSD ? "/sdqu_logo.png" : "/tk_logo.png";
  const defAmt = (user?.school === "sdqu" || students[0]?.school === "sdqu") ? 500000 : 400000;

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)); }, []);
  const load = () => { if (sid) api.get(`/spp?student_id=${sid}`).then((r) => setList(r.data)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);

  const openEdit = (m) => {
    const bill = list.find((b) => b.month === m && String(b.year) === String(year));
    setEdit(m);
    setEf({ status: bill?.status || "Belum", paid_date: bill?.paid_date ? bill.paid_date.slice(0, 10) : "", note: bill?.note || "", amount: bill?.amount || defAmt });
  };
  const save = async () => {
    try {
      await api.post("/spp", { student_id: sid, month: edit, year, amount: Number(ef.amount), status: ef.status, paid_date: ef.paid_date || "", note: ef.note });
      toast.success(`SPP ${edit} diperbarui`);
      setEdit(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Kelola SPP" />
      <div className="px-5 pt-4 pb-10">
        <button data-testid="spp-report-link" onClick={() => navigate("/kelola/spp-laporan")}
          className="w-full mb-4 bg-green-700 text-white font-semibold py-3 rounded-2xl active:scale-95 transition-transform inline-flex items-center justify-center gap-2">
          <ChartBar size={18} weight="bold" /> Buka Laporan SPP {isSD ? "SD" : "TK"} (Ringkasan per Kelas)
        </button>
        <p className="text-xs text-stone-400 mb-1">Pilih Tahun</p>
        <select data-testid="spp-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 mb-3 outline-none focus:border-primary font-semibold">
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="relative mb-3" data-testid="spp-search-wrap">
          <input data-testid="spp-search" value={q} onChange={(e) => { const v = e.target.value; setQ(v); const cur = students.find((x) => x.id === sid); if (cur && v !== cur.name) setSid(""); }} placeholder="Cari nama siswa..."
            className="w-full bg-white border border-stone-200 rounded-2xl py-3 px-4 outline-none focus:border-primary text-sm" />
        </div>
        {q.trim() && !sid && (
          <div data-testid="spp-suggest" className="bg-white border border-stone-200 rounded-2xl mb-4 overflow-hidden divide-y divide-stone-100 shadow-sm">
            {students.filter((s) => (s.name || "").toLowerCase().includes(q.trim().toLowerCase())).slice(0, 12).map((s) => (
              <button key={s.id} data-testid={`spp-suggest-${s.id}`} onClick={() => { setSid(s.id); setQ(s.name); }}
                className="w-full text-left px-4 py-2.5 text-sm active:bg-primary/5 flex items-center justify-between gap-2">
                <span className="font-semibold text-stone-700 truncate">{s.name}</span>
                <span className="text-stone-400 text-xs shrink-0">{s.kelas}</span>
              </button>
            ))}
            {students.filter((s) => (s.name || "").toLowerCase().includes(q.trim().toLowerCase())).length === 0 && (
              <p className="px-4 py-3 text-sm text-stone-400">Tidak ada siswa cocok.</p>
            )}
          </div>
        )}

        {!sid && !q.trim() && (
          <div data-testid="spp-empty-hint" className="text-center text-stone-400 text-sm py-12 border-2 border-dashed border-stone-200 rounded-2xl">
            Ketik nama siswa di atas untuk menampilkan data SPP.
          </div>
        )}

        {sid && (<>
        <div className="rounded-2xl overflow-x-auto border border-green-200 shadow-sm" data-testid="manage-spp-table">
          <table className="w-full border-collapse text-sm min-w-[560px]">
            <thead>
              <tr className="bg-green-800 text-white text-left">
                <th className="px-3 py-2.5 font-bold whitespace-nowrap">Bulan</th>
                <th className="px-2 py-2.5 font-bold text-center whitespace-nowrap">Status</th>
                <th className="px-3 py-2.5 font-bold whitespace-nowrap">Nominal</th>
                <th className="px-2 py-2.5 font-bold text-center whitespace-nowrap">Tgl Bayar</th>
                <th className="px-3 py-2.5 font-bold">Ket</th>
                <th className="px-2 py-2.5 font-bold text-center">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const bill = list.find((b) => b.month === m && String(b.year) === String(year));
                const st = bill?.status || "Belum";
                return (
                  <tr key={m} data-testid={`manage-spp-row-${m}`} className={i % 2 === 0 ? "bg-green-50" : "bg-white"}>
                    <td className="px-3 py-2.5 font-semibold text-stone-700 border-t border-green-100 whitespace-nowrap">{MONTHS_SHORT[i]} {String(year).slice(-2)}</td>
                    <td className="px-2 py-2.5 text-center border-t border-green-100">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${badge(st)}`}>{label(st)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-stone-700 font-semibold text-xs border-t border-green-100 whitespace-nowrap">{rupiah(bill?.amount ?? 400000)}</td>
                    <td className="px-2 py-2.5 text-center text-stone-500 text-xs border-t border-green-100 whitespace-nowrap">{fmtPaid(bill?.paid_date)}</td>
                    <td className="px-3 py-2.5 text-stone-500 text-xs border-t border-green-100">{bill?.note || "-"}</td>
                    <td className="px-2 py-2.5 text-center border-t border-green-100">
                      <button data-testid={`edit-spp-${m}`} onClick={() => openEdit(m)}
                        className="w-8 h-8 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center active:scale-90 transition-transform"><PencilSimple size={15} weight="bold" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <button data-testid="spp-view-btn" onClick={() => setViewOpen(true)}
          className="w-full mt-4 border-2 border-primary text-primary font-semibold py-3 rounded-2xl active:scale-95 transition-transform">
          View (Pratinjau SPP Orang Tua)
        </button>
        </>)}
      </div>

      {viewOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center" onClick={() => setViewOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-4 max-h-[92vh] overflow-y-auto" data-testid="spp-view-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg text-stone-800">Pratinjau SPP</h3>
              <button onClick={() => setViewOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="bg-stone-50 rounded-2xl p-3 mb-3 flex items-center gap-3">
              <img src={logo} alt="logo" className="w-10 h-10 object-contain" data-testid="spp-view-logo" />
              <div>
                <p className="font-heading font-bold text-stone-800 leading-tight">{students.find((x) => x.id === sid)?.name || "—"}</p>
                <p className="text-stone-500 text-sm">Kelas {students.find((x) => x.id === sid)?.kelas || "—"} · {isSD ? "SDQu" : "TK"} Muslim Kids</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-green-200 shadow-sm">
              <img src={logo} alt="" aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 h-52 object-contain z-10" style={{ opacity: 0.15 }} data-testid="spp-view-watermark" />
              <table className="relative w-full border-collapse text-[11px] min-w-[360px]">
                <thead>
                  <tr className="bg-green-800 text-white text-left">
                    <th className="px-3 py-1.5 font-bold whitespace-nowrap">Bulan</th>
                    <th className="px-2 py-1.5 font-bold text-center whitespace-nowrap">SPP</th>
                    <th className="px-2 py-1.5 font-bold text-center whitespace-nowrap">Tgl Bayar</th>
                    <th className="px-3 py-1.5 font-bold">Ket</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map((m, i) => {
                    const bill = list.find((b) => b.month === m && String(b.year) === String(year));
                    const st = bill?.status || "Belum";
                    return (
                      <tr key={m} className={i % 2 === 0 ? "bg-green-50/70" : "bg-white/70"}>
                        <td className="px-3 py-1 font-semibold text-stone-700 border-t border-green-100 whitespace-nowrap">{MONTHS_SHORT[i]} {String(year).slice(-2)}</td>
                        <td className="px-2 py-1 text-center border-t border-green-100 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded-lg font-bold ${badge(st)}`}>{rupiah(bill?.amount ?? defAmt)}</span>
                        </td>
                        <td className="px-2 py-1 text-center text-stone-500 border-t border-green-100 whitespace-nowrap">{fmtPaid(bill?.paid_date)}</td>
                        <td className="px-3 py-1 text-stone-500 border-t border-green-100">{bill?.note || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setEdit(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">SPP {edit} {year}</h3>
              <button onClick={() => setEdit(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-400 mb-1.5">Status Pembayaran</p>
            <div className="flex gap-2 mb-3">
              {STATUS.map((s) => (
                <button key={s} data-testid={`spp-status-${s}`} onClick={() => setEf({ ...ef, status: s })}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${ef.status === s ? badge(s) + " border-current" : "bg-white border-stone-200 text-stone-500"}`}>{s}</button>
              ))}
            </div>
            <p className="text-xs text-stone-400 mb-1">Nominal (Rp)</p>
            <input data-testid="spp-amount" type="number" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2.5 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-400 mb-1">Tanggal Bayar</p>
            <input data-testid="spp-paid-date" type="date" value={ef.paid_date} onChange={(e) => setEf({ ...ef, paid_date: e.target.value })}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2.5 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-400 mb-1">Keterangan</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {QUICK_NOTES.map((q) => (
                <button key={q} data-testid={`spp-quicknote-${q}`} onClick={() => setEf({ ...ef, note: q })}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary active:scale-95 transition-transform">{q}</button>
              ))}
            </div>
            <textarea data-testid="spp-note" value={ef.note} onChange={(e) => setEf({ ...ef, note: e.target.value })} rows={2}
              placeholder="Teks bebas / pilih cepat di atas" className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2.5 px-3 mb-4 outline-none focus:border-primary resize-none" />
            <button data-testid="spp-status-save" onClick={save}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan</button>
          </div>
        </div>
      )}
    </div>
  );
}
